import React, { useEffect, useRef, useState } from 'react'
import cx from 'classnames'
import KeywordMatchBullets from './KeywordMatchBullets'
import KeywordMatchCount from './KeywordMatchCount'

export interface Match { title: string, sectionId: string, path: string }

interface KeywordMatchListProps {
  matches: Match[]
  sectionId: string
  keyword: string
  aol: string
}

const MAX_TO_SHOW = 2

function renderWithBoldKeyword (text: string, keyword: string): React.ReactNode {
  const trimmed = keyword.trim()
  if (trimmed === '') return text
  const safeKeyword = trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  if (safeKeyword === '') return text
  const regex = new RegExp(safeKeyword, 'gi')
  const parts = []
  let lastIndex = 0
  let matchResult
  let keyIdx = 0
  while ((matchResult = regex.exec(text)) !== null) {
    if (matchResult.index > lastIndex) {
      parts.push(text.slice(lastIndex, matchResult.index))
    }
    parts.push(
      <strong key={keyIdx++}>
        {text.slice(matchResult.index, regex.lastIndex)}
      </strong>
    )
    lastIndex = regex.lastIndex
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }
  return parts.length > 0 ? parts : text
}

export function KeywordMatchList ({
  matches = [],
  keyword,
  aol
}: KeywordMatchListProps): JSX.Element | null {
  const listRef = useRef<HTMLDivElement>(null)
  const [expanded, setExpanded] = useState(false)

  const handleFocusIn = (event: FocusEvent): void => {
    if (!expanded && listRef.current !== null) {
      setExpanded(listRef.current.contains(event.target as Node))
    }
  }

  const handleFocusOut = (): void => {
    setExpanded(false)
  }

  useEffect(() => {
    window.addEventListener('focusin', handleFocusIn)
    window.addEventListener('focusout', handleFocusOut)

    return () => {
      window.removeEventListener('focusin', handleFocusIn)
      window.removeEventListener('focusout', handleFocusOut)
    }
  }, [listRef.current])

  const handleExpand = (): void => {
    setExpanded(true)
  }

  const handleCollapse = (): void => {
    setExpanded(false)
  }

  const hasMatches = matches.length > 0
  const moreCount = matches.length - MAX_TO_SHOW
  const collapsedMaxHeight = 70

  if (!hasMatches) return null

  const bullets = matches.map((match: Match) => {
    const { title, path } = match
    return {
      basicTitle: title,
      title: renderWithBoldKeyword(title, keyword),
      url: path,
      keyword
    }
  })

  const containerStyle = {
    maxHeight: expanded ? 'none' : collapsedMaxHeight
  }

  return (
    <div
      ref={listRef}
      className={cx(
        'mt1 br2 mv2 f6 w-100 pointer flex flex-column relative ba minh-min-content pa3 ph4',
        {
          'overflow-auto z1055': expanded,
          'overflow-hidden': !expanded,
          [`bg-aol-${aol}-light b--aol-${aol}`]: aol !== '',
          'bg-white': aol === ''
        }
      )}
      style={containerStyle}
      onMouseEnter={handleExpand}
      onMouseLeave={handleCollapse}
    >
      <KeywordMatchBullets
        bullets={bullets}
        collapsed={!expanded}
        maxHeight={!expanded ? collapsedMaxHeight : undefined}
        aol={aol}
      />
      {!expanded && <KeywordMatchCount count={moreCount} />}
    </div>
  )
}

export default KeywordMatchList
